import {
  FormSelectNative as UIKitFormSelectNative,
  type FormSelectNativeProps as UIKitFormSelectNativeProps,
} from "@circle-react-uikit/Form/FormSelectNative";
import { Select as CompassSelect } from "@circleco/compass/components/Select";
import { omit } from "lodash";
import { useEffect, type ReactNode } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- Re-export for compatibility
export type {
  Option,
  FormSelectNativeProps,
} from "@circle-react-uikit/Form/FormSelectNative";

type HorizontalLayoutRatio = "50/50" | "40/60" | "33/67" | "30/70" | "25/75";

interface CompassFormSelectNativeProps extends UIKitFormSelectNativeProps {
  "data-testid"?: string;
  description?: ReactNode;
  error?: string;
  horizontalLayoutRatio?: HorizontalLayoutRatio;
  invalid?: boolean;
  label?: ReactNode;
  labelTooltip?: ReactNode;
  layout?: "horizontal" | "vertical";
  required?: boolean;
  searchable?: boolean;
}

const COMPASS_INJECTED_KEYS = [
  "description",
  "error",
  "horizontalLayoutRatio",
  "invalid",
  "label",
  "labelTooltip",
  "layout",
  "searchable",
] as const;

const normalizeToString = (value: unknown): string | undefined => {
  if (value == null || value === "") {
    return undefined;
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return undefined;
};

export function FormSelectNative(
  props: Readonly<CompassFormSelectNativeProps>,
) {
  const {
    name,
    options,
    prompt,
    isPromptDisabled = true,
    disabled: isDisabled,
    defaultValue,
    label,
    labelTooltip,
    description,
    error,
    invalid: isInvalid,
    required: isRequired,
    rules,
    layout,
    horizontalLayoutRatio,
    "data-testid": dataTestId,
    searchable: isSearchable = false,
  } = props;

  const compassLayout = layout === "horizontal" ? "horizontal" : undefined;

  const isCompassEnabled = useIsCircleV4FeatureEnabled(
    "circle_v4_form_elements_enabled",
  );

  const { control, setValue } = useFormContext();
  const { field } = useController({ name, control, rules });
  const fieldValue = normalizeToString(field.value);

  const compassOptions = options.map(opt => ({
    value: String(opt.value),
    label: opt.label,
  }));

  if (prompt && !isPromptDisabled && !isSearchable) {
    compassOptions.unshift({ value: "", label: prompt });
  }

  // Keep Compass Select in controlled mode while matching native <select> behavior:
  // if RHF has no value yet, use default/first option and sync it into form state.
  const fallbackValue = prompt
    ? undefined
    : normalizeToString(defaultValue ?? options[0]?.value);
  const selectedValue = fieldValue ?? fallbackValue;

  useEffect(() => {
    if (!isCompassEnabled) {
      return;
    }

    if (fieldValue == null && fallbackValue != null) {
      setValue(name, fallbackValue, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [fallbackValue, fieldValue, isCompassEnabled, name, setValue]);

  if (!isCompassEnabled) {
    return <UIKitFormSelectNative {...omit(props, COMPASS_INJECTED_KEYS)} />;
  }

  // Use `?? null` so `selectedOption` is always null-or-Option, never undefined.
  // Base UI treats `value={undefined}` as uncontrolled and ignores subsequent value
  // prop changes. When options load asynchronously (e.g. coupons after paywall selection),
  // the find temporarily returns undefined — passing null instead keeps the Select in
  // controlled mode so it correctly updates once the matching option arrives.
  const selectedOption =
    selectedValue != null
      ? (compassOptions.find(opt => opt.value === selectedValue) ?? null)
      : null;

  const handleValueChange = (
    newOption: { value: string; label: string } | null,
  ) => {
    if (newOption === null) {
      field.onChange("");
      return;
    }

    field.onChange(newOption.value);
  };

  return (
    <CompassSelect
      name={name}
      data-testid={dataTestId}
      options={compassOptions}
      searchable={isSearchable}
      placeholder={prompt}
      disabled={isDisabled}
      label={label}
      labelTooltip={labelTooltip}
      labelProps={{ required: isRequired }}
      description={description}
      error={error}
      invalid={isInvalid}
      required={isRequired}
      value={selectedOption}
      onValueChange={handleValueChange}
      {...(compassLayout != null ? { layout: compassLayout } : {})}
      {...(compassLayout != null && horizontalLayoutRatio != null
        ? { horizontalLayoutRatio }
        : {})}
    />
  );
}

FormSelectNative.displayName = "Form.SelectNative";
